import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { filter, pairwise } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { AnalyticEventNames } from '../enums/analytics-event-models';

export const gtag = (...args: any[]) => (window as any)['dataLayer'].push(...args);
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private mixpanelInitialized = false;
  private gaInitialized = false;
  public currentRoute = new BehaviorSubject<string>('');
  private sessionStart: number = 0;
  private sessionEnd: number = 0;
  private sessionTimeout = 2 * 60 * 60 * 1000; // 30 minutes

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((evt: any) => {
      this.currentRoute.next(this.getRouteName(this.activatedRoute.root));
      this.checkSession();

    });

    this.startSessionTracking();

  }

  private checkSession() {
    const currentTime = Date.now();
    if (this.isLocalStorageAvailable()) {
      if (currentTime - this.sessionEnd > this.sessionTimeout) {
        this.startNewSession();
      } else {
        this.sessionEnd = currentTime;
        localStorage.setItem('sessionEnd', this.sessionEnd.toString());
      }
    }
  }



  init() {
    // this.initMixpanel(environment.mixPanelKey);
    // this.initGoogleAnalytics(environment.gaKey);
  }


  initGoogleAnalytics(gaTrackingId: string) {
    if (!this.gaInitialized) {
      if (typeof gtag === 'undefined') {
        console.error('Google Analytics script not loaded.');
        return;
      }
      // gtag('js', new Date());
      gtag('config', 'AW-10841969382');
      this.gaInitialized = true;
    }
  }

  trackEvent(eventType: string, eventData: any) {
    // if (environment.production) {
    //   const defaultEventData = {
    //     device_platform: this.getDeviceInfo(),
    //     user_region: '',
    //     currentRoute: this.currentRoute
    //   };
    //   const mergedEventData = { ...defaultEventData, ...eventData };

    //   // if (this.mixpanelInitialized) {
    //   //   this.trackMixpanelEvent(eventType, mergedEventData);
    //   // }
    //   if (this.gaInitialized) {
    //     this.trackGoogleAnalyticsEvent(eventType, mergedEventData);
    //   }
    // }
  }

  private trackGoogleAnalyticsEvent(eventType: string, eventData: any) {
    try {
      gtag('event', eventType, {
        event_category: 'Web Event',
        event_label: eventData.toString()
      });

    } catch (error) {
      console.log(`error: $error`);

    }
  }

  private getRouteName(route: ActivatedRoute): string {
    let routeName = route.routeConfig?.path || '';
    while (route.firstChild) {
      route = route.firstChild;
      if (route.routeConfig?.path) {
        routeName += `/${route.routeConfig.path}`;
      }
    }
    return routeName;
  }

  private getDeviceInfo(): string {
    const userAgent = navigator.userAgent;
    let browserName = 'Unknown Browser';
    let platform = 'Unknown OS';

    if (userAgent.indexOf('Firefox') > -1) {
      browserName = 'Mozilla Firefox';
    } else if (userAgent.indexOf('SamsungBrowser') > -1) {
      browserName = 'Samsung Internet';
    } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
      browserName = 'Opera';
    } else if (userAgent.indexOf('Trident') > -1) {
      browserName = 'Microsoft Internet Explorer';
    } else if (userAgent.indexOf('Edge') > -1) {
      browserName = 'Microsoft Edge';
    } else if (userAgent.indexOf('Chrome') > -1) {
      browserName = 'Google Chrome';
    } else if (userAgent.indexOf('Safari') > -1) {
      browserName = 'Apple Safari';
    }

    if (userAgent.indexOf('Win') > -1) {
      platform = 'Windows';
    } else if (userAgent.indexOf('Mac') > -1) {
      platform = 'MacOS';
    } else if (userAgent.indexOf('Linux') > -1) {
      platform = 'Linux';
    } else if (userAgent.indexOf('Android') > -1) {
      platform = 'Android';
    } else if (userAgent.indexOf('like Mac') > -1) {
      platform = 'iOS';
    }

    return `${platform} - ${browserName}`;
  }

  private startSessionTracking() {
    if (!this.isLocalStorageAvailable()) {


      return;
    }
    const storedSessionStart = localStorage.getItem('sessionStart');
    const storedSessionEnd = localStorage.getItem('sessionEnd');

    if (storedSessionStart && storedSessionEnd) {
      this.sessionStart = parseInt(storedSessionStart, 10);
      this.sessionEnd = parseInt(storedSessionEnd, 10);

      const currentTime = Date.now();
      if (currentTime - this.sessionEnd > this.sessionTimeout) {
        // Start a new session if the last session ended more than 30 minutes ago
        this.startNewSession();
      }
    } else {
      this.startNewSession();
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.addEventListener('beforeunload', this.handlePageUnload.bind(this));
    window.addEventListener('unload', this.handlePageUnload.bind(this));
  }

  private startNewSession() {
    this.sessionStart = Date.now();
    this.sessionEnd = this.sessionStart;
    localStorage.setItem('sessionStart', this.sessionStart.toString());
    localStorage.setItem('sessionEnd', this.sessionEnd.toString());
  }

  private handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.sessionEnd = Date.now();
      localStorage.setItem('sessionEnd', this.sessionEnd.toString());
      this.trackSession();
    } else {
      const currentTime = Date.now();
      if (currentTime - this.sessionEnd > this.sessionTimeout) {
        // Start a new session if the last session ended more than 30 minutes ago
        this.startNewSession();
      }
    }
  }
  private isLocalStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  private handlePageUnload() {
    this.sessionEnd = Date.now();
    if (this.isLocalStorageAvailable()) {
      localStorage.setItem('sessionEnd', this.sessionEnd.toString());
    }
    localStorage.setItem('sessionEnd', this.sessionEnd.toString());
    this.trackSession();
  }

  private trackSession() {
    const sessionDuration = this.sessionEnd - this.sessionStart;

    const eventData = {
      session_duration: sessionDuration,
      current_page: this.currentRoute,
      device_platform: this.getDeviceInfo(),
      user_region: ''
    };

    this.trackEvent(AnalyticEventNames.WEB_HUNCH_SESSION_TRACKER, eventData);
  }
}
